import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Calendar } from 'lucide-react';
import axios from 'axios';
import { AchievementBadge } from './AchievementBadge';
import { getAchievementLevel, getNextAchievement } from '../utils/achievements';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserProfileModal = ({ isOpen, onClose, userId, displayName, profilePicture }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && userId) {
      fetchProfile();
    }
  }, [isOpen, userId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/users/${userId}/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      // Use fallback data
      setProfile({
        display_name: displayName,
        profile_picture: profilePicture,
        days_fasted: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const level = profile ? getAchievementLevel(profile.days_fasted) : null;
  const nextLevel = profile ? getNextAchievement(profile.days_fasted) : null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm" data-testid="user-profile-modal">
        <DialogHeader>
          <DialogTitle className="sr-only">User Profile</DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-pulse text-muted-foreground">Loading...</div>
          </div>
        ) : profile && level && (
          <div className="flex flex-col items-center text-center py-4">
            {/* Profile Picture with Achievement Badge */}
            <div className="relative mb-4">
              <div className={`w-32 h-32 rounded-full overflow-hidden flex items-center justify-center ring-4 ${level.color.replace('bg-', 'ring-')}/30`}
                   style={{ backgroundColor: profile.profile_picture ? 'transparent' : `hsl(var(--primary) / 0.1)` }}>
                {profile.profile_picture ? (
                  <img 
                    src={profile.profile_picture} 
                    alt={profile.display_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-primary font-display text-5xl">
                    {profile.display_name.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              
              {/* Achievement Badge - prominent position */}
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2">
                <AchievementBadge daysFasted={profile.days_fasted} size="lg" />
              </div>
            </div>

            {/* User Name */}
            <h3 className="font-display text-2xl text-foreground mt-6">
              {profile.display_name}
            </h3>
            
            {/* Achievement Title */}
            <div className={`flex items-center gap-2 mt-2 ${level.textColor} font-medium`}>
              <span>{level.name}</span>
            </div>
            
            {/* Days Fasted Stats */}
            <div className="flex items-center gap-2 mt-1 text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span className="text-sm">
                {profile.days_fasted} {profile.days_fasted === 1 ? 'day' : 'days'} fasted
              </span>
            </div>

            {/* Achievement Description */}
            <p className="text-sm text-muted-foreground mt-3 px-4">
              {level.description}
            </p>

            {/* Progress to next level */}
            {nextLevel && (
              <div className="w-full mt-6 px-4">
                <div className="flex justify-between text-xs text-muted-foreground mb-2">
                  <span>{profile.days_fasted} days</span>
                  <span>{nextLevel.days} days</span>
                </div>
                <div className="h-2 bg-secondary rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${nextLevel.color} transition-all duration-500`}
                    style={{ 
                      width: `${Math.min(100, ((profile.days_fasted - level.days) / (nextLevel.days - level.days)) * 100)}%` 
                    }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {nextLevel.daysRemaining} more {nextLevel.daysRemaining === 1 ? 'day' : 'days'} to reach <span className={nextLevel.textColor}>{nextLevel.name}</span>
                </p>
              </div>
            )}

            {/* Max level message */}
            {!nextLevel && profile.days_fasted >= 100 && (
              <div className="mt-4 px-4 py-2 bg-amber-50 rounded-lg">
                <p className="text-sm text-amber-700 font-medium">
                  🏆 Maximum achievement reached!
                </p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserProfileModal;
